<template>
	<div class="term">
		<h2>{{ $t('MAIN_CONTENT.commonContent.newEntryTitleCase') }}</h2>
		<div class="form-group">
			<label for="term-new">{{ $t('MAIN_CONTENT.commonContent.term') }}</label>
			<input id="term-new"
							v-model="editingTerm.TERM"
							type="text"
							class="form-control">
		</div>
		<div class="form-group">
			<label for="definition-new">{{ $t('MAIN_CONTENT.commonContent.definition') }}</label>
			<ckeditor4 v-model="editingTerm.DEFINITION"
								:editor="editor"
								:config="editorConfig">
			</ckeditor4>
		</div>
		<template v-if="!!$route.params.courseSectionGUID && !!selectedStudent.STUDENT_RID && !!$store.state.accessToken && !!$store.state.clientID">
			<file-uploader
				ref="fileUpload"
				:nameHeader="$t( 'MAIN_CONTENT.commonContent.name' )"
				:sizeHeader="$t( 'MAIN_CONTENT.commonContent.size' )"
				:removeHeader="$t( 'MAIN_CONTENT.commonContent.remove' )"
				:dropSingleFile="$t( 'MAIN_CONTENT.commonContent.dropSingleFile' )"
				:dropMultipleFiles="$t( 'MAIN_CONTENT.commonContent.dropMultipleFiles' )"
				:selectSingleFile="$t( 'MAIN_CONTENT.commonContent.selectSingleFile' )"
				:selectMultipleFiles="$t( 'MAIN_CONTENT.commonContent.selectMultipleFiles' )"
				:attachFile="$t( 'MAIN_CONTENT.commonContent.attachFile' )"
				:attachFiles="$t( 'MAIN_CONTENT.commonContent.attachFiles' )"
				:files="files"
				:postAction="fileUploadToken"
				:URL="externalFileData"
				@saveGoogleFilesData="saveGoogleFileData"
				@saveDropboxFilesData="saveDropboxFileData"
				@saveOneDriveFilesData="saveOneDriveFileData"
				@updateFiles="updateValue"
			>
			</file-uploader>
		</template>
		<div class="form-group text-right">
			<button class="btn btn-default" @click="closeForm">{{ $t('commonText.cancel') }}</button>
			<button class="btn btn-success" @click="save" :disabled="!editingTerm.TERM">{{ $t('commonText.save') }}</button>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';

	// Shared components in vuecommons
	import FileUploader from 'COMMONS/shared/FileUploader.vue';

	// API's
	import GlossaryEntries from '../../../api/GlossaryEntries';
	import ExternalFilePicker from '../../../api/ExternalFilePicker';
	import ExternalFilesPost from '../../../api/ExternalFilesPost';
	import FileUploadTokenResource from '../../../api/FileUploadToken';

	import ckeditor4 from '../../ckeditor4.vue';
	import editorConfigObj from '../../shared/editorConfigObj';

	import { buildFileData } from './buildFileData';

	/**
		* @vue-prop {Object} [term={}] - Initial term to be edited.
		* @vue-prop {String} [type='glossaries'] - String to state what type of glossary to access
		* @vue-data {Object} editingTerm - Term/entry in edit mode.
		* @vue-data {Object} editorConfig - Configuration of the editor.
		* @vue-data {Object} editor - editor type for ckeditor
		* @vue-data {Object} courseSectionGUID - course GUID for API's
		* @vue-computed {Object} selectedStudent - selectedStudent object from vuex
		* @vue-computed {String} accessToken - accessToken from vuex
	*/
	export default {
		props: {
			term: {
				type: Object,
				default: function() {
					return {};
				}
			},
			type: {
				type: String,
				default: 'glossaries'
			}
		},
		data() {
			return {
				fileUploadToken: '',
				editingTerm: this.term,
				editor: null,
				files: [],
				editorConfig: editorConfigObj,
				externalFileData: {
					API_ORIGIN_URL:							'',
					DISTRICT_ID:								'',
					DROPBOX_API_CLIENT_ID:			'',
					DROPBOX_API_CLIENT_SECRET:	'',
					GOOGLE_API_APP_ID:					'',
					GOOGLE_API_CLIENT_ID:				'',
					GOOGLE_API_CLIENT_SECRET:		'',
					ONEDRIVE_API_CLIENT_ID:			'',
					ONEDRIVE_API_CLIENT_SECRET:	''
				},
				courseSectionGUID: this.$route.params.courseSectionGUID
			};
		},
		computed: {
			...mapGetters(['selectedStudent', 'accessToken'])
		},
		components: {
			ckeditor4,
			FileUploader
		},
		methods: {
			fetchFileUploadToken() {
				const FileUploadToken = new FileUploadTokenResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				FileUploadToken.get().then(() => {
					this.fileUploadToken = FileUploadToken.getResponseData();
				});
			},
			closeForm() {
				this.$emit('closeForm');
			},
			updateValue(files) {
				this.files = files;
			},
			getExternalData() {
				const self = this;

				const external = new ExternalFilePicker({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID
					}
				});

				external.get().then(response => {
					self.$set(self, 'externalFileData', external.getResponseData());
				});
			},
			save() {
				const self = this;

				let files = [];

				for(let i in self.$refs.fileUpload.files) {
					files.push(self.$refs.fileUpload.files[i].response.data);
				}

				const glossary = new GlossaryEntries({
					routeParams: {
						courseSectionGuid: self.$route.params.courseSectionGUID,
						glossaryID: self.$route.params.glossaryID,
						glossaryType: self.type
					},
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
					},
					data: JSON.stringify({
						term: self.editingTerm.TERM,
						definition: self.editingTerm.DEFINITION,
						files: files
					})
				});

				glossary.post().then(response => {
					self.$set(self, 'glossary', glossary.getResponseData());
					self.isLoading = false;

					if(!glossary.getResponseData().error) {
						self.closeForm();
					}
				});
			},
			saveGoogleFileData(file) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'googleDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						file_id: file.id,
						authorization: file.authToken,
						file_name: file.name,
						file_mime: file.mimeType,
						webContentLink: file.webContentLink,
						webViewLink: file.webViewLink
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in self.files) {
						let file = self.files[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						self.files.push(dataObj);
					}
				});
			},
			saveDropboxFileData(file) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'dropbox',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						authorization: file.authToken,
						file_name: file.name,
						web_content_link: file.link
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in self.files) {
						let file = self.files[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						self.files.push(dataObj);
					}
				});
			},
			saveOneDriveFileData(file) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'oneDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						file_name: file.name,
						web_content_link: file['@content.downloadUrl']
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();

					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in self.files) {
						let file = self.files[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						self.files.push(dataObj);
					}
				});
			}
		},
		created() {
			this.fetchFileUploadToken();
			this.getExternalData();
		}
	};
</script>
